// NotificationsSceneDOM.js - Complete Notifications Management with Template Implementation
// Integrates desktop-notifications.html + mobile-notifications.html templates

import BaseMenuScene from "./BaseMenuScene.js";
import { auth } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class NotificationsSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "NotificationsSceneDOM" });
        this.domElement = null;
        this.isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        this.notifications = [];
        this.currentFilter = 'all';
    }

    async create() {
        console.log('🔔 NotificationsSceneDOM: Creating complete notifications management with templates');
        
        // Load comprehensive template CSS
        if (!document.getElementById('all-templates-exact-css')) {
            const link = document.createElement('link');
            link.id = 'all-templates-exact-css';
            link.rel = 'stylesheet';
            link.href = '/ALL_TEMPLATES_EXACT.css?v=' + Date.now();
            document.head.appendChild(link);
            console.log('🎨 Loaded comprehensive template CSS for notifications');
        }
        
        // Load notifications data
        await this.loadNotifications();
        
        // Create the notifications interface
        this.createNotificationsInterface();
        
        // Initialize with enterprise services (centralized event binding and navigation)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay', // STANDARDIZED: Same ID across all scenes
            bindEvents: true,
            enableNavigation: true
        });
        
        // Setup scene-specific event listeners (filters, mark read, delete)
        this.setupEventListeners();
        
        this.sceneReady = true;
    }

    async loadNotifications() {
        // Mock notifications data - replace with real Firebase data
        this.notifications = [
            {
                id: '1',
                type: 'performance',
                title: 'New PPT Hero Achievement!',
                message: 'You\'ve reached PPT Hero status with a score of 83,500!',
                timestamp: new Date(Date.now() - 1000 * 60 * 30),
                read: false,
                priority: 'high'
            },
            {
                id: '2',
                type: 'system',
                title: 'Weekly Performance Report',
                message: 'Your weekly analytics report is ready for review.',
                timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
                read: true,
                priority: 'medium'
            },
            {
                id: '3',
                type: 'social',
                title: 'New Hall of Fame Ranking',
                message: 'You\'ve moved up to #1 in the global leaderboard!',
                timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
                read: false,
                priority: 'high'
            }
        ];
    }

    createNotificationsInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay'; // STANDARDIZED: Use same ID as all other scenes
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Load template CSS
        this.loadTemplateCSS();

        if (this.isMobile) {
            overlay.innerHTML = this.createMobileNotificationsHTML();
        } else {
            overlay.innerHTML = this.createDesktopNotificationsHTML();
        }

        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        // Register with overlay manager for proper cleanup - UPDATED ID
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'NotificationsSceneDOM');
    }

    loadTemplateCSS() {
        // Load desktop executive CSS (same as all other scenes)
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        // Load notifications-specific CSS
        if (!document.getElementById('notifications-template-css')) {
            const link = document.createElement('link');
            link.id = 'notifications-template-css';
            link.rel = 'stylesheet';
            link.href = '/notifications-template.css'; // NO query parameter
            document.head.appendChild(link);
        }
    }

    createDesktopNotificationsHTML() {
        const unreadCount = this.notifications.filter(n => !n.read).length;
        
        return `
            <!-- Desktop Notifications Template Structure -->
            <div class="app-container">
                <!-- Sidebar Navigation -->
                <aside class="sidebar">
                    <div class="logo-section">
                        <div class="logo">
                            <div class="logo-icon">D</div>
                            <div class="logo-text">DeckOps.ai</div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <a href="#" class="nav-item" data-action="dashboard">
                            <span>📊</span>
                            <span>Dashboard</span>
                        </a>
                        <a href="#" class="nav-item" data-action="profile">
                            <span>👤</span>
                            <span>Profile</span>
                        </a>
                        <a href="#" class="nav-item active" data-action="notifications">
                            <span>🔔</span>
                            <span>Notifications</span>
                            ${unreadCount > 0 ? `<span class="nav-badge">${unreadCount}</span>` : ''}
                        </a>
                        <a href="#" class="nav-item" data-action="analytics">
                            <span>📈</span>
                            <span>Analytics</span>
                        </a>
                        <a href="#" class="nav-item" data-action="logout">
                            <span>🚪</span>
                            <span>Logout</span>
                        </a>
                    </nav>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <header class="header">
                        <div class="header-content">
                            <div class="header-left">
                                <h1 class="page-title">Notifications</h1>
                                <span class="notification-count">${this.notifications.length} total, ${unreadCount} unread</span>
                            </div>
                            <div class="header-actions">
                                <button class="filter-btn" data-filter="unread">Show Unread</button>
                                <button class="mark-all-btn" data-action="mark-all-read">Mark All Read</button>
                                <button class="settings-btn" data-action="notification-settings">⚙️</button>
                            </div>
                        </div>
                    </header>
                    
                    <!-- Filter Tabs -->
                    <div class="filter-tabs">
                        <button class="filter-tab active" data-filter="all">All</button>
                        <button class="filter-tab" data-filter="performance">Performance</button>
                        <button class="filter-tab" data-filter="system">System</button>
                        <button class="filter-tab" data-filter="social">Social</button>
                    </div>
                    
                    <!-- Notifications List -->
                    <div class="notifications-container">
                        ${this.createNotificationsList()}
                    </div>
                </main>
            </div>
        `;
    }

    createMobileNotificationsHTML() {
        const unreadCount = this.notifications.filter(n => !n.read).length;
        
        return `
            <!-- Mobile Notifications Template Structure -->
            <header class="header">
                <div class="header-left">
                    <button class="back-button" data-action="back">
                        <span>←</span>
                    </button>
                    <h1 class="header-title">Notifications</h1>
                </div>
                <button class="mark-all-button" data-action="mark-all-read">Mark All Read</button>
            </header>
            
            <div class="content">
                <!-- Filter Pills -->
                <div class="filter-pills">
                    <button class="filter-pill active" data-filter="all">
                        All <span class="badge">${this.notifications.length}</span>
                    </button>
                    <button class="filter-pill" data-filter="unread">
                        Unread <span class="badge">${unreadCount}</span>
                    </button>
                    <button class="filter-pill" data-filter="performance">Performance</button>
                    <button class="filter-pill" data-filter="system">System</button>
                    <button class="filter-pill" data-filter="social">Social</button>
                </div>
                
                <!-- Notifications List -->
                <div class="notifications-list">
                    ${this.createMobileNotificationsList()}
                </div>
            </div>
        `;
    }

    createNotificationsList() {
        const filteredNotifications = this.getFilteredNotifications();
        
        if (filteredNotifications.length === 0) {
            return `
                <div class="empty-state">
                    <div class="empty-icon">🔔</div>
                    <h3 class="empty-title">No notifications</h3>
                    <p class="empty-description">You're all caught up! New notifications will appear here.</p>
                </div>
            `;
        }

        return filteredNotifications.map(notification => `
            <div class="notification-item ${notification.read ? 'read' : 'unread'}" data-id="${notification.id}">
                <div class="notification-indicator ${notification.priority}"></div>
                <div class="notification-icon">${this.getNotificationIcon(notification.type)}</div>
                <div class="notification-content">
                    <h4 class="notification-title">${notification.title}</h4>
                    <p class="notification-message">${notification.message}</p>
                    <span class="notification-time">${this.formatTimestamp(notification.timestamp)}</span>
                </div>
                <div class="notification-actions">
                    ${!notification.read ? '<button class="mark-read-btn" data-action="mark-read">Mark Read</button>' : ''}
                    <button class="delete-btn" data-action="delete">×</button>
                </div>
            </div>
        `).join('');
    }

    createMobileNotificationsList() {
        return this.createNotificationsList(); // Same structure for mobile
    }

    getFilteredNotifications() {
        switch (this.currentFilter) {
            case 'unread':
                return this.notifications.filter(n => !n.read);
            case 'performance':
            case 'system':
            case 'social':
                return this.notifications.filter(n => n.type === this.currentFilter);
            default:
                return this.notifications;
        }
    }

    getNotificationIcon(type) {
        switch (type) {
            case 'performance': return '🎯';
            case 'system': return '⚙️';
            case 'social': return '👥';
            default: return '🔔';
        }
    }

    formatTimestamp(timestamp) {
        const now = new Date();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (minutes < 60) {
            return `${minutes}m ago`;
        } else if (hours < 24) {
            return `${hours}h ago`;
        } else {
            return `${days}d ago`;
        }
    }

    setupEventListeners() {
        // NOTE: Navigation actions (dashboard, profile, analytics) are now handled by 
        // baseSceneIntegration.initializeTemplateScene() via UnifiedEventBindingSystem
        // Only bind notification-specific actions here
        
        // Filter actions
        this.domElement.querySelectorAll('[data-filter]').forEach(filter => {
            filter.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleFilter(filter.dataset.filter);
            });
        });
        
        // Notification-specific actions (mark read, delete, etc.)
        this.domElement.querySelectorAll('[data-action]').forEach(button => {
            const action = button.dataset.action;
            // Only bind notification-specific actions; navigation is handled centrally
            if (['mark-all-read', 'mark-read', 'delete', 'notification-settings'].includes(action)) {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.handleNotificationAction(action, e.target);
                });
            }
        });
    }

    handleNotificationAction(action, element) {
        console.log('🔔 NotificationsSceneDOM: Handling notification action:', action);
        
        switch (action) {
            case 'mark-all-read':
                this.markAllAsRead();
                break;
            case 'mark-read':
                this.markAsRead(element.closest('.notification-item').dataset.id);
                break;
            case 'delete':
                this.deleteNotification(element.closest('.notification-item').dataset.id);
                break;
            case 'notification-settings':
                this.openNotificationSettings();
                break;
        }
    }

    handleFilter(filter) {
        this.currentFilter = filter;
        
        // Update filter appearance
        this.domElement.querySelectorAll('[data-filter]').forEach(filterEl => {
            filterEl.classList.toggle('active', filterEl.dataset.filter === filter);
        });
        
        // Refresh notifications list
        const container = this.domElement.querySelector('.notifications-container, .notifications-list');
        if (container) {
            container.innerHTML = this.isMobile ? this.createMobileNotificationsList() : this.createNotificationsList();
        }
    }

    markAllAsRead() {
        this.notifications.forEach(notification => {
            notification.read = true;
        });
        this.refreshNotificationsList();
        this.showNotification('All notifications marked as read', 'success');
    }

    markAsRead(notificationId) {
        const notification = this.notifications.find(n => n.id === notificationId);
        if (notification) {
            notification.read = true;
            this.refreshNotificationsList();
        }
    }

    deleteNotification(notificationId) {
        this.notifications = this.notifications.filter(n => n.id !== notificationId);
        this.refreshNotificationsList();
        this.showNotification('Notification deleted', 'info');
    }

    refreshNotificationsList() {
        const container = this.domElement.querySelector('.notifications-container, .notifications-list');
        if (container) {
            container.innerHTML = this.isMobile ? this.createMobileNotificationsList() : this.createNotificationsList();
        }
        
        // Re-bind event listeners for new elements
        this.setupEventListeners();
    }

    openNotificationSettings() {
        // Navigate to profile settings - handled by NavigationActionHandler
        if (this.handleTemplateAction) {
            this.handleTemplateAction('profile');
        } else {
            this.startScene('ProfileSceneDOMTemplate');
        }
    }

    forceCleanupBeforeTransition() {
        console.log('🔔 NotificationsSceneDOM: Force cleanup before transition...');
        
        // Use overlay manager for proper cleanup
        templateOverlayManager.prepareForTransition('AuthenticatedMenuSceneDOM');
    }

    showNotification(message, type = 'info') {
        console.log(`🔔 Notification (${type}):`, message);
        if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        console.log('🔔 NotificationsSceneDOM: Shutting down and cleaning up overlays...');
        
        // CRITICAL: Force remove overlay immediately
        const notificationsOverlay = document.getElementById('notifications-scene-overlay');
        if (notificationsOverlay) {
            notificationsOverlay.remove();
            console.log('🔔 NotificationsSceneDOM: Overlay removed');
        }
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        super.shutdown();
    }
}
